import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchSubmissionDetail, buildEditDiff } from '../../lib/admin.js';
import {
  findSimilarBibliography,
  findSimilarScholars,
  findSimilarOrganizations,
  findSimilarTeaching,
  findSimilarResearchTools,
  findSimilarEngagements,
  findSimilarWhatsNew,
} from '../../lib/findSimilar.js';
import SimilarEntriesWarning from '../common/SimilarEntriesWarning.jsx';

// Run the matching duplicate-detection RPC for the pending row's module
// so the admin sees existing entries that look like this one before
// approving it. Edit suggestions skip the check (they're modifications
// to an already-published row, not a new candidate).
const SIMILAR_KIND_BY_MODULE = {
  bibliography: 'bibliography',
  scholar: 'scholars',
  organization: 'organizations',
  teaching_material: 'teaching',
  research_tool: 'research_tools',
  broader_engagement: 'broader_engagements',
  whats_new: 'whats_new',
};

async function findSimilarForRow(module, row) {
  if (!row) return [];
  switch (module) {
    case 'bibliography':
      return findSimilarBibliography({ title: row.title, year: row.year, excludeId: row.id });
    case 'scholar':
      return findSimilarScholars({ name: row.name, email: row.email_public, excludeId: row.id });
    case 'organization':
      return findSimilarOrganizations({ name: row.name, website: row.website, excludeId: row.id });
    case 'teaching_material':
      return findSimilarTeaching({ title: row.title, url: row.url, excludeId: row.id });
    case 'research_tool':
      return findSimilarResearchTools({ name: row.name, url: row.url, excludeId: row.id });
    case 'broader_engagement':
      return findSimilarEngagements({ title: row.title, url: row.url, excludeId: row.id });
    case 'whats_new':
      return findSimilarWhatsNew({ title: row.title, dateStart: row.date_start, excludeId: row.id });
    default:
      return [];
  }
}

function formatValue(v) {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

export default function SubmissionDetail({ item, reviewer, onApprove, onReject, busy }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [similarCandidates, setSimilarCandidates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    setShowRejectForm(false);
    setRejectNote('');
    setSimilarCandidates([]);
    if (!item) return undefined;
    fetchSubmissionDetail({ module: item.module, id: item.id })
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        // Only check for duplicates on new submissions, not edit
        // suggestions (those modify an already-existing row).
        if (d?.module && d.module !== 'bibliography_edit') {
          findSimilarForRow(d.module, d.row)
            .then((rows) => !cancelled && setSimilarCandidates(rows))
            .catch(() => {});
        }
      })
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [item?.module, item?.id]);

  if (!item) {
    return (
      <div className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
        {t('admin.selectSubmission')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/40 bg-red-950/30 p-6 text-red-200">{error}</div>
    );
  }

  if (!detail) {
    return (
      <div className="rounded-lg border border-white/10 bg-navy-base/30 p-8 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  const { module, row, original, submitter } = detail;
  const isEdit = module === 'bibliography_edit';
  const titleField = isEdit ? original?.title : row.title || row.name;

  return (
    <div className="rounded-lg border border-white/10 bg-navy-base/40 p-6">
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-gold">
          {t(`admin.modules.${module}`)}
        </span>
        {row.created_at && (
          <span className="text-xs text-ink-muted">
            {new Date(row.created_at).toLocaleString()}
          </span>
        )}
      </div>

      <h2 className="font-serif text-2xl font-semibold text-cream">{titleField || t('admin.untitled')}</h2>

      {submitter && (
        <p className="mt-1 text-sm text-ink-secondary">
          {t('admin.submittedBy')}{' '}
          <span className="text-cream">
            {submitter.display_name || submitter.email}
          </span>
        </p>
      )}

      {/* Body */}
      <div className="mt-6">
        {isEdit ? <EditDiff original={original} edit={row} /> : <RowFields row={row} module={module} />}
      </div>

      {/* Duplicate-detection panel — only on new submissions, not edits. */}
      {!isEdit && SIMILAR_KIND_BY_MODULE[module] && (
        <SimilarEntriesWarning
          candidates={similarCandidates}
          kind={SIMILAR_KIND_BY_MODULE[module]}
        />
      )}

      {isEdit && row.reason && (
        <section className="mt-6">
          <h3 className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-gold">
            {t('bibliography.form.reason')}
          </h3>
          <p className="whitespace-pre-line text-sm leading-relaxed text-ink-primary">
            {row.reason}
          </p>
        </section>
      )}

      {/* Actions */}
      <footer className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-white/5 pt-6">
        {showRejectForm ? (
          <div className="w-full">
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
              {t('admin.rejectionNote')}
            </label>
            <textarea
              rows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="form-input"
              placeholder={t('admin.rejectionNotePlaceholder')}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setShowRejectForm(false)}
                disabled={busy}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md border border-red-500/50 bg-red-950/30 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                onClick={() => onReject({ note: rejectNote })}
                disabled={busy}
              >
                {busy ? t('common.loading') : t('admin.confirmReject')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setShowRejectForm(true)}
              disabled={busy}
            >
              {t('common.reject')}
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={onApprove}
              disabled={busy}
            >
              {busy ? t('common.loading') : t('common.approve')}
            </button>
          </>
        )}
      </footer>
    </div>
  );
}

function EditDiff({ original, edit }) {
  const { t } = useTranslation();
  const diff = buildEditDiff(original, edit);
  if (diff.length === 0) {
    return <p className="text-sm text-ink-secondary">{t('admin.noChanges')}</p>;
  }
  return (
    <div className="space-y-4">
      {diff.map(({ field, before, after }) => (
        <div key={field}>
          <div className="mb-1 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">
            {field}
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="rounded-md border border-red-500/30 bg-red-950/20 p-3 text-sm text-red-200">
              <div className="mb-1 text-[0.65rem] uppercase tracking-[0.12em] text-red-300/70">
                {t('admin.before')}
              </div>
              {formatValue(before)}
            </div>
            <div className="rounded-md border border-emerald-500/30 bg-emerald-950/20 p-3 text-sm text-emerald-200">
              <div className="mb-1 text-[0.65rem] uppercase tracking-[0.12em] text-emerald-300/70">
                {t('admin.after')}
              </div>
              {formatValue(after)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RowFields({ row, module }) {
  // Pick a sensible set of fields per module to render. Hides nulls.
  const fields = FIELDS[module] ?? Object.keys(row);
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-[140px_1fr]">
      {fields.map((f) => {
        const v = row[f];
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
        return (
          <div key={f} className="contents">
            <dt className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">{f}</dt>
            <dd className="text-ink-primary">{formatValue(v)}</dd>
          </div>
        );
      })}
    </dl>
  );
}

const FIELDS = {
  bibliography: [
    'type', 'authors', 'year', 'language_display', 'doi', 'url',
    'publisher', 'place', 'edition', 'journal', 'volume', 'issue', 'pages',
    'editors', 'booktitle', 'conference', 'keywords', 'abstract',
  ],
  scholar: ['name', 'position', 'department', 'university', 'website', 'email_public', 'city', 'country'],
  organization: ['name', 'type', 'description', 'website', 'country', 'city', 'issn', 'publisher', 'frequency', 'open_access'],
  teaching_material: ['title', 'type', 'description', 'author', 'institution', 'url', 'language', 'level'],
  research_tool: ['name', 'type', 'description', 'url', 'institution'],
  broader_engagement: ['title', 'category', 'author', 'year', 'description', 'url'],
  whats_new: ['title', 'type', 'description', 'url', 'date_start', 'date_end', 'location'],
};
